import { useMemo, useState } from "react";
import type { CustomColumn } from "../types/types";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ExtensionTwoToneIcon from "@mui/icons-material/ExtensionTwoTone";

interface ColumnItem {
  customColumnId: number;
  label: string;
  isVisible: boolean;
}

interface Props {
  customColumns: CustomColumn[];
  /** 現在表示中のカスタム列ID（表示順） */
  initialVisibleIds: number[];
  color: PaletteColor;
  isDarkMode: boolean;
  onClose: () => void;
  /** 表示順を保持したカスタム列IDの配列を返す */
  onSave: (visibleIds: number[]) => void;
}

/**
 * 保存済みの表示ID（順番付き）と現在のカスタム列定義から初期リストを組み立てる。
 * 表示中の列は保存された順番で先頭に、未選択の列はその後ろに定義順で並べる。
 */
function buildItems(
  customColumns: CustomColumn[],
  visibleIds: number[],
): ColumnItem[] {
  const byId = new Map(customColumns.map((c) => [c.customColumnId, c]));

  // 表示中の列（保存順・存在するものだけ）
  const visibleItems = visibleIds
    .map((id) => byId.get(id))
    .filter((c): c is CustomColumn => !!c)
    .map((c) => ({
      customColumnId: c.customColumnId,
      label: c.columnName,
      isVisible: true,
    }));

  // 未選択の列を定義順で末尾に追加
  const visibleSet = new Set(visibleIds);
  const hiddenItems = customColumns
    .filter((c) => !visibleSet.has(c.customColumnId))
    .map((c) => ({
      customColumnId: c.customColumnId,
      label: c.columnName,
      isVisible: false,
    }));

  return [...visibleItems, ...hiddenItems];
}

/**
 * TaskList（リスト形式）の TaskItem に表示するカスタム列を設定するダイアログ。
 * 設定はプロジェクト（リスト）ごとに localStorage へ保存される。
 */
export const TaskItemCustomColumnDialog = ({
  customColumns,
  initialVisibleIds,
  color,
  isDarkMode,
  onClose,
  onSave,
}: Props) => {
  const accentColor = PaletteColorHex[color];
  const [items, setItems] = useState<ColumnItem[]>(() =>
    buildItems(customColumns, initialVisibleIds),
  );

  const headerStyle = {
    background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
  };
  const bgStyle = isDarkMode
    ? { backgroundColor: "#1e1e1e", color: "#e0e0e0" }
    : { backgroundColor: "#ffffff", color: "#212121" };
  const borderColor = isDarkMode ? "#333" : "#e5e7eb";
  const textSecondary = isDarkMode ? "#9e9e9e" : "#757575";

  const visibleCount = useMemo(
    () => items.filter((i) => i.isVisible).length,
    [items],
  );

  const moveUp = (index: number) => {
    if (index === 0) return;
    setItems((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index: number) => {
    setItems((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const toggleVisible = (index: number) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, isVisible: !item.isVisible } : item,
      ),
    );
  };

  const handleSave = () => {
    // 表示中の列のみを、現在の並び順で保存する
    const visibleIds = items
      .filter((i) => i.isVisible)
      .map((i) => i.customColumnId);
    onSave(visibleIds);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative rounded-lg shadow-xl w-full max-w-sm mx-4 flex flex-col overflow-hidden"
        style={{
          ...bgStyle,
          border: isDarkMode ? "1px solid #000" : "1px solid #fff",
          maxHeight: "80vh",
        }}
      >
        {/* ヘッダー */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={headerStyle}
        >
          <div className="flex items-center gap-2 text-white">
            <ExtensionTwoToneIcon style={{ fontSize: 20 }} />
            <span className="font-semibold text-sm">表示するカスタム項目</span>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* 本文 */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-xs mb-3" style={{ color: textSecondary }}>
            タスクの「担当・期限」の下に表示するカスタム項目を選びます。1行に2項目まで表示され、並び順はこの一覧の順番になります。
          </p>

          {customColumns.length === 0 ? (
            <div
              className="text-sm text-center py-8 rounded-lg"
              style={{
                color: textSecondary,
                border: `1px dashed ${borderColor}`,
              }}
            >
              このプロジェクトにはカスタム項目がありません。
              <br />
              全般設定からカスタム項目を追加できます。
            </div>
          ) : (
            items.map((item, index) => (
              <div
                key={item.customColumnId}
                className="flex items-center justify-between px-3 py-2 rounded-lg mb-1"
                style={{ border: `1px solid ${borderColor}` }}
              >
                <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                  <input
                    type="checkbox"
                    checked={item.isVisible}
                    onChange={() => toggleVisible(index)}
                    className="cursor-pointer flex-shrink-0"
                  />
                  <span
                    className="text-sm truncate"
                    style={{ color: item.isVisible ? undefined : textSecondary }}
                  >
                    {item.label}
                  </span>
                </label>
                <div className="flex gap-0.5 flex-shrink-0">
                  <button
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-20"
                    style={{ color: textSecondary }}
                    aria-label="上へ移動"
                  >
                    <ArrowUpwardIcon style={{ fontSize: 14 }} />
                  </button>
                  <button
                    onClick={() => moveDown(index)}
                    disabled={index === items.length - 1}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-20"
                    style={{ color: textSecondary }}
                    aria-label="下へ移動"
                  >
                    <ArrowDownwardIcon style={{ fontSize: 14 }} />
                  </button>
                </div>
              </div>
            ))
          )}

          {customColumns.length > 0 && (
            <p className="text-xs mt-2" style={{ color: textSecondary }}>
              表示中: {visibleCount} 項目
            </p>
          )}
        </div>

        {/* フッター */}
        <div
          className="flex items-center justify-end gap-2 px-4 py-3 border-t"
          style={{ borderColor }}
        >
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50 transition-colors"
            style={{ borderColor }}
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-1.5 text-sm rounded text-white transition-colors"
            style={{ backgroundColor: accentColor }}
          >
            保存する
          </button>
        </div>
      </div>
    </div>
  );
};
